import { UseMutationOptions } from '@tanstack/react-query';
import { SendInput } from 'domains/campaigns/types';
import { CampaignType, CuredApiPaths, ApiMethods } from 'models/enums';
import { PathWithID } from 'models/types';
import useMutation from '../useMutation';
import MutationKeys from './keys';

export type CreateCampaignMutationInput = {
    name: string;
    type: CampaignType;
    sends: SendInput[];
};

type CreateCampaignResponse = { id: string };

/**
 * Creates a new campaign or publishes an existing draft campaign.
 * - POST /campaign/ for publishing a new campaign
 * - PUT /campaign/:campaignId for publishing an existing draft campaign
 * @param campaignId Optional campaign ID. If provided, publishes the existing draft campaign.
 * @param mutateOptions react-query useMutation option
 * @returns the react-query useMutation results
 * See https://react-query-v3.tanstack.com/reference/useMutation for options and return value
 */
const useCreateCampaign = (
    campaignIdOrOptions?: string | UseMutationOptions<CreateCampaignResponse, Error, CreateCampaignMutationInput>,
    mutateOptions?: UseMutationOptions<CreateCampaignResponse, Error, CreateCampaignMutationInput>
) => {
    // Handle both old and new API signatures
    const campaignId = typeof campaignIdOrOptions === 'string' ? campaignIdOrOptions : undefined;
    const options = typeof campaignIdOrOptions === 'object' ? campaignIdOrOptions : mutateOptions;

    // If campaignId is provided, use UPDATE_CAMPAIGN to publish the draft with PUT method
    // Otherwise, use CREATE_CAMPAIGN to create a new campaign with POST method
    const mutationKey = campaignId ? MutationKeys.UPDATE_CAMPAIGN : MutationKeys.CREATE_CAMPAIGN;
    const apiPath = campaignId ? new PathWithID(CuredApiPaths.CAMPAIGNS, campaignId) : CuredApiPaths.CAMPAIGNS;
    const method = campaignId ? ApiMethods.PUT : ApiMethods.POST;

    return useMutation<CreateCampaignMutationInput, CreateCampaignResponse>(mutationKey, apiPath, options, method);
};

export default useCreateCampaign;
